import type { CursorPosition } from '@corner/shared';
import { getCollabColor } from '../../lib/collabColors';

interface LiveCursorsProps {
  /** Map of remote cursors keyed by userId */
  cursors: Map<string, CursorPosition>;
  /** Current page number being viewed (only show cursors on same page) */
  currentPage?: number;
}

/**
 * Overlay that renders remote collaborator cursors on the document canvas.
 * Position absolutely over the document viewer container.
 */
export default function LiveCursors({ cursors, currentPage = 1 }: LiveCursorsProps) {
  if (cursors.size === 0) return null;

  return (
    <div style={OVERLAY}>
      {[...cursors.values()]
        .filter((c) => c.pageNumber === currentPage)
        .map((cursor) => {
          const cc = getCollabColor(cursor.userId);
          return (
          <div
            key={cursor.userId}
            style={{
              position: 'absolute',
              left: `${cursor.x}%`,
              top: `${cursor.y}%`,
              pointerEvents: 'none',
              zIndex: 50,
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              transition: 'left 60ms linear, top 60ms linear',
              willChange: 'left, top',
            }}
          >
            {/* Cursor arrow SVG */}
            <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
              <path
                d="M1 1L1 14L5 10L9 17L11 16L7 9L12 8L1 1Z"
                fill={cc.bg}
                stroke="#FFFFFF"
                strokeWidth="0.8"
              />
            </svg>
            {/* Name label */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '3px 8px',
                backgroundColor: cc.bg,
                borderRadius: 4,
                borderTopLeftRadius: 0,
                marginLeft: 10,
                marginTop: -2,
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  fontFamily: '"Geist", system-ui, sans-serif',
                  fontSize: 11,
                  fontWeight: 500,
                  color: cc.text,
                }}
              >
                {cursor.displayName}
              </span>
            </div>
          </div>
          );
        })}
    </div>
  );
}

const OVERLAY: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  zIndex: 40,
};
